import React, { useEffect } from "react";
import { tenantLogin, getCurrentUser } from "../api/auth";
import { createAuthStore } from "../lib/createAuthStore";
import { queryClient } from "../lib/queryClient";
import { resetRealtimeConnection } from "../hooks/useRealtimeEvents";

const TENANT_SURFACE = ["/admin", "/driver", "/onboarding"];

// One-time impersonation token hand-off from Platform Admin (URL hash — never sent to the server)
function adoptImpersonationToken() {
  try {
    const url = new URL(window.location.href);
    const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
    const impToken = hash.get("__impersonation_token") || url.searchParams.get("__impersonation_token");
    if (!impToken) return;
    localStorage.setItem("auth_token", impToken);
    localStorage.removeItem("refresh_token");
    localStorage.setItem("impersonation_active", "1");
    url.searchParams.delete("__impersonation_token");
    window.history.replaceState({}, "", url.pathname + url.search);
  } catch {}
}

export const useTenantAuthStore = createAuthStore({
  tokenKey: "auth_token",
  refreshKey: "refresh_token",
  clearKeys: ["auth_user", "app_mode"],
  fetchMe: getCurrentUser,
  onBeforeLoad: adoptImpersonationToken,
  shouldLoad: () => { const p = window.location.pathname; return p === "/login" || TENANT_SURFACE.some(s => p.startsWith(s)); },
  onLoadError: "keep", // token refresh is handled by the API interceptor
  extend: (set) => ({
    currentMode: localStorage.getItem("app_mode") || "admin",
    switchMode: (mode) => { localStorage.setItem("app_mode", mode); set({ currentMode: mode }); },
  }),
});

export function AuthProvider({ children }) {
  const load = useTenantAuthStore(s => s.load);
  useEffect(() => { load(); }, [load]);
  return <>{children}</>;
}

export function useAuth() {
  const s = useTenantAuthStore();
  const { user } = s;

  const login = async (email, password) => {
    const res = await tenantLogin(email, password);
    const { access_token, refresh_token, user: userData } = res.data;
    s.setSession(access_token, refresh_token, userData);
    queryClient.clear();
    resetRealtimeConnection();
    return userData;
  };

  const logout = () => {
    s.clearSession();
    useTenantAuthStore.setState({ currentMode: "admin" });
    queryClient.clear();
    resetRealtimeConnection();
  };

  const hasCapability = (cap) => {
    if (!user?.role_name) return false;
    if (user.role_name === "Owner") return true;
    const token = localStorage.getItem("auth_token");
    if (!token) return false;
    try { return (JSON.parse(atob(token.split(".")[1])).capabilities || []).includes(cap); } catch { return false; }
  };

  const isOwner = user?.role_name === "Owner";
  const isAdmin = user?.role_name === "Admin";
  const isDriver = user?.role_name === "Driver";
  const isStaff = user?.role_name === "Staff";
  const canAccessDriverMode = isOwner || isDriver;
  const canAccessAdminMode = isOwner || isAdmin || isStaff;

  const getPostLoginPath = () => {
    if (isDriver && !canAccessAdminMode) return "/driver";
    if (s.currentMode === "driver" && canAccessDriverMode) return "/driver";
    return "/admin";
  };

  return {
    user, loading: s.loading, login, logout, hasCapability, isAuthenticated: !!user,
    currentMode: s.currentMode, switchMode: s.switchMode, canSwitchMode: isOwner,
    canAccessDriverMode, canAccessAdminMode,
    isOwner, isAdmin, isDriver, isStaff,
    getPostLoginPath,
  };
}
